import React, { useState, useEffect } from 'react'
import { Redirect } from 'react-router-dom'
import { ServerConfig } from 'config'
import axios from 'axios'
import { useSelector } from 'react-redux'

import Select from 'react-select'
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import { PDFExport } from "@progress/kendo-react-pdf"
import PageTemplate from "./pageTemplate"
import { utc_timestamp_to_local_datetime_string } from "../../utils/datetime_utc_local"
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { Typography, Grid, Button, CircularProgress, Box  } from '@material-ui/core'
import InfoBanner from 'components/common/InfoBanner'

import _ from 'lodash'
import logo_mono from 'assets/img/logo/logo_circle_mono.png'

// ── testing toggles ──────────────────────────────────────────────────────────
const SHOW_CERT_ID = false
const SHOW_SITE    = true

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    fontWeight: 'bold',
    borderBottom: '2px solid black',
    paddingBottom: '4px',
    marginBottom: '4px',
  },
  tableRow: {
    borderBottom: '1px solid #ccc',
    padding: '4px 0',
  },
  tableCell: {
    padding: '4px 8px',
  },
  mobileCard: {
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    padding: '12px',
    marginBottom: '10px',
  },
  mobileCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  mobileCardLabel: {
    fontWeight: 'bold',
    marginRight: '10px',
    color: '#666',
  },
	datePickerPopper: {
        zIndex: '999 !important',
    },
  infoBanner: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: 8,
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
  },
}))

// ── helpers ───────────────────────────────────────────────────────────────────

const BAG_STATE_OPTIONS = [
  { value: 2, label: 'Received (Picked Up)' },
  { value: 3, label: 'Processed' },
]

const NO_FILTER_OPTION = { label: 'All Sites', value: '' }

function firstOfMonthDate() {
  const d = new Date()
  d.setDate(1)
  return d
}

// ── component ─────────────────────────────────────────────────────────────────

const EcosackReport = () => {
  const ref = React.createRef()
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))

  const token  = useSelector(state => state.auth.access_token)
  const tenant = useSelector(state => state.auth.tenant)

  // cert IDs (optional filter, not auto-selected)
  const [availableCertIDs, setAvailableCertIDs] = useState([])
  const [selectedCertID,   setSelectedCertID]   = useState({ label: 'All', value: '' })

  // sites (optional filter, not auto-selected)
  const [availableSites, setAvailableSites] = useState([])
  const [selectedSite,   setSelectedSite]   = useState(NO_FILTER_OPTION)

  // filters — now Date objects, matching DR6
  const [bagState,  setBagState]  = useState(null)
  const [startDate, setStartDate] = useState(firstOfMonthDate())
  const [endDate,   setEndDate]   = useState(new Date())

  // report state
  const [reportItems, setReportItems] = useState([])
  const [reportMeta,  setReportMeta]  = useState(null)
  const [isLoading,   setIsLoading]   = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // ── effects ───────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchCertIDList(token)
    fetchSiteList(token)
  }, [token])

  // ── API helpers ───────────────────────────────────────────────────────────────

  function authConfig() {
    const config = { headers: { 'Content-Type': 'application/json' } }
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config
  }

  async function fetchCertIDList(token) {
    const res = await axios.get(`${ServerConfig.SERVER_URL}/tenant/cert_ids`, authConfig())
    const options = [{ label: 'All', value: '' }]
    for (let idx in res.data) {
      options.push({ value: res.data[idx], label: res.data[idx] })
    }
    setAvailableCertIDs(options)
  }

  async function fetchSiteList(token) {
    const res = await axios.get(`${ServerConfig.SERVER_URL}/tenant/tenant_sites`, authConfig())
    const options = [NO_FILTER_OPTION]
    for (let idx in res.data) {
      const site = res.data[idx]
      options.push({
        value: site.id,
        label: site.city ? `${site.name} (${site.city}, ${site.state})` : site.name,
      })
    }
    setAvailableSites(options)
  }

    async function generateReport() {
      if (!bagState) {
        alert('Please select a status before generating the report.')
        return
      }

      setIsLoading(true)
      setHasSearched(true)

    try {
      const start_ts_utc = startDate.getTime()
      const end_ts_utc   = endDate.getTime()

      const body = {
        state: bagState.value,
        start_ts_utc,
        end_ts_utc,
      }
      if (SHOW_CERT_ID && selectedCertID.value) body.cert_id = selectedCertID.value
      if (SHOW_SITE && selectedSite.value)      body.site_id = selectedSite.value

      const res = await axios.post(
        `${ServerConfig.SERVER_URL}/trackingcode/bag_activity_report`,
        body,
        authConfig()
      )

      setReportItems(Array.isArray(res.data) ? res.data : [])
      setReportMeta({
        ts_create: Date.now(),
        bag_state: bagState.label,
        state_value: bagState.value,
        cert_id:   selectedCertID.value || 'All',
        site_name: selectedSite.label   || 'All Sites',
        start_ts:  start_ts_utc,
        end_ts:    end_ts_utc,
        tenant_business_name:  tenant?.business_name || '',
        tenant_address_street: tenant?.street         || '',
        tenant_city_state_zip: `${tenant?.city || ''}, ${tenant?.state || ''}, ${tenant?.zipcode || ''}`,
        tenant_contact_name:   tenant?.first_name     || '',
      })
    } catch (err) {
      console.error('Failed to generate Bag Activity Report', err)
      alert('Error generating Bag Activity Report.')
      setReportItems([])
      setReportMeta(null)
    }

    setIsLoading(false)
  }

  if (!token) return <Redirect to="/login" />

  // ── render helpers ────────────────────────────────────────────────────────────

  function relevantTimestamp(item) {
    return reportMeta.state_value === 2
      ? item?.lifecycle?.picked_up
      : item?.lifecycle?.processed
  }

  // received reports show where the bag came in, processed reports where it was processed
  function relevantSiteName(item) {
    return reportMeta.state_value === 2
      ? item.receiving_tenant?.receiving_site?.name || item.name_tenant_receiving
      : item.processing_tenant?.processing_site?.name || item.name_tenant_processing
  }

  const bottomRowXs = SHOW_CERT_ID ? 6 : 12

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <>        
        <InfoBanner
          style={{'width': '100%', 'marginBottom': '20px'}}
          items={[
            "This report reflects live data — results may change as bags are picked up or processed.",
          ]}
        />
      <Grid container spacing={3} style={{'borderRadius': '20px', 'backgroundColor': 'white', 'padding': '20px'}}>
 
        <Grid xs={12} style={{'marginBottom': '10px'}}>
          <b>EcoSack Report:</b>
        </Grid>

        {/* Site — full-width primary field, matching DR6's Cert-ID row */}
        <Grid xs={12} style={{'marginBottom': '20px', 'paddingRight': '20px'}}>
          <Typography variant="h2">Site:</Typography>
          <Select
            name="site_id"
            id="site_id"
            options={availableSites}
            value={selectedSite}
            onChange={(e) => setSelectedSite(e)}
          />
        </Grid>

        {/* Start / End Date — react-datepicker, matching DR6 exactly */}
        <Grid xs={4} style={{'marginBottom': '20px'}}>
          <Typography variant="h2">Start-Date (incl.):</Typography>
          <DatePicker selected={startDate} onChange={(date) => {
            setStartDate(date)
          }}
            showTimeSelect
            dateFormat="MMMM d, yyyy h:mm aa"
            className="form-select"
            popperClassName={classes.datePickerPopper}
            />
        </Grid>

        <Grid xs={4} style={{'marginBottom': '20px'}}>
          <Typography variant="h2">End-Date (incl.):</Typography>
          <DatePicker selected={endDate} onChange={(date) => {
            setEndDate(date)
          }}
            showTimeSelect
            dateFormat="MMMM d, yyyy h:mm aa"
            className="form-select"
            popperClassName={classes.datePickerPopper}
            />
        </Grid>

        {/* Cert-ID + Status — bottom row, share space, expand full if Cert-ID is hidden */}
        {SHOW_CERT_ID &&
          <Grid xs={bottomRowXs} style={{'marginBottom': '20px'}}>
            <Typography variant="h2">Cert-ID (optional):</Typography>
            <Select
              name="cert_id"
              id="cert_id"
              options={availableCertIDs}
              value={selectedCertID}
              onChange={(e) => setSelectedCertID(e)}
            />
          </Grid>
        }

        <Grid xs={bottomRowXs} style={{'marginBottom': '20px'}}>
          <Typography variant="h2">Status:</Typography>
          <Select
            name="bag_state"
            id="bag_state"
            options={BAG_STATE_OPTIONS}
            value={bagState}
            onChange={(e) => setBagState(e)}
            placeholder="Choose a status..."
          />
        </Grid>

        <Grid xs={12} style={{'paddingTop': '20px', 'textAlign': isMobile ? 'center' : 'right'}}>
          {isLoading
            ? <CircularProgress size={24} />
            : <Button fullWidth={isMobile} onClick={generateReport}>Generate Report</Button>
          }
        </Grid>
      </Grid>

      {/* ── PDF Export ── */}
      <Grid xs={12} style={{'marginTop': '30px'}}>
        {reportMeta &&
          <>
            <Button onClick={() => { if (ref.current) ref.current.save() }}>
              Download PDF
            </Button>

            <PDFExport
              scale={0.5}
              pageTemplate={PageTemplate}
              forcePageBreak=".page-break"
              multiPage="true"
              paperSize="letter"
              fileName={`Ecosack_${reportMeta.ts_create}.pdf`}
              margin={{top: '1.5cm', bottom: '2.5cm', right: '0.5cm', left: '0.5cm'}}
              ref={ref}
            >
              <Grid container spacing={3}
                style={{'padding': '50px', 'border': '1px solid black', 'backgroundColor': 'white'}}
              >
                <Grid xs={8}>
                  <h1 style={{'marginBottom': '30px'}}>EcoSack Report — {reportMeta.bag_state}</h1>
                </Grid>
                <Grid xs={4} style={{'textAlign': 'right', 'paddingTop': '30px'}}>
                  <img src={logo_mono} height="40" alt="logo" />
                </Grid>

                <Grid xs={12} style={{'marginBottom': '20px', 'borderTop': '1px solid gray', 'paddingTop': '10px'}}>
                  Created: {utc_timestamp_to_local_datetime_string(reportMeta.ts_create)}<br/>
                </Grid>

                <Grid xs={3}>
                  {SHOW_CERT_ID && <>Cert-ID:<br/></>}
                  Site:<br/><br/>
                  Business-Name:<br/>
                  Address:<br/><br/><br/>
                  Contact:
                </Grid>
                <Grid xs={7}>
                  {SHOW_CERT_ID && <>{reportMeta.cert_id}<br/></>}
                  {reportMeta.site_name}<br/><br/>
                  {reportMeta.tenant_business_name}<br/>
                  {reportMeta.tenant_address_street}<br/>
                  {reportMeta.tenant_city_state_zip}<br/><br/>
                  {reportMeta.tenant_contact_name}
                </Grid>

                <Grid xs={12} style={{'borderTop': '1px solid gray', 'marginTop': '10px', 'marginBottom': '10px'}} />

                <Grid xs={6}>
                  <b>From:</b> {utc_timestamp_to_local_datetime_string(reportMeta.start_ts)}
                </Grid>
                <Grid xs={6}>
                  <b>To:</b> {utc_timestamp_to_local_datetime_string(reportMeta.end_ts)}
                </Grid>
                <Grid xs={12}><b>Found {reportItems.length} bags</b></Grid>
                <Grid xs={12} style={{'marginBottom': '20px'}} />

                <Grid container xs={12}
                  style={{
                    'fontWeight': 'bold',
                    'borderTop': '1px solid black',
                    'borderBottom': '1px solid black',
                    'paddingTop': '6px',
                    'paddingBottom': '6px',
                    'backgroundColor': '#f5f5f5',
                  }}
                >
                  <Grid xs={3} style={{'paddingLeft': '4px'}}>Bag Code</Grid>
                  <Grid xs={2}>Sticker Code</Grid>
                  <Grid xs={4}>{reportMeta.state_value === 2 ? 'Receiving Site' : 'Processing Site'}</Grid>
                  <Grid xs={3}>Date</Grid>
                </Grid>

                {reportItems.map((item) => (
                  <Grid container xs={12} key={item.code}
                    style={{'borderBottom': '1px solid #e0e0e0', 'padding': '4px 0'}}
                  >
                    <Grid xs={3} style={{'paddingLeft': '4px'}}>{item.code}</Grid>
                    <Grid xs={2}>{item.stickercode}</Grid>
                    <Grid xs={4}>{relevantSiteName(item) || '—'}</Grid>
                    <Grid xs={3}>
                      {relevantTimestamp(item)
                        ? utc_timestamp_to_local_datetime_string(relevantTimestamp(item))
                        : '—'}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </PDFExport>
          </>
        }
      </Grid>
    </>
  )
}

export default EcosackReport